// SupportTicketManagement.jsx
import React, { useState, useEffect } from "react";
import {
  AiOutlineUser,
  AiOutlineClockCircle,
  AiOutlineCheckCircle,
  AiOutlineEye,
  AiOutlineSearch,
  AiOutlineFilter,
  AiOutlinePaperClip,
  AiOutlineExclamationCircle,
} from "react-icons/ai";
import {
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Input,
  Button,
  FormGroup,
  Label,
  Alert,
} from "reactstrap";
import "../../../styles/dashboard/SuperAdmin/SupportTicketManagement.css";

export default function SupportTicketManagement({ role = 'admin' }) {
  const roleLC = role?.toLowerCase();
  const isOrgUser = roleLC === 'orguser';
  const isSuperAdmin = roleLC === 'superadmin';
  const isAdmin = roleLC === 'admin';
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState(tickets);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterOrg, setFilterOrg] = useState("");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [viewModal, setViewModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState("");
  const [saving, setSaving] = useState(false);
  const [successAlert, setSuccessAlert] = useState(false);
  const [activeTab, setActiveTab] = useState('communication');
  // Message thread state
  const [ticketMessages, setTicketMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [messageFile, setMessageFile] = useState(null);
  const [sendingMessage, setSendingMessage] = useState(false);

  // Create ticket form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createFormData, setCreateFormData] = useState({ subject: '', description: '', organizationId: '' });
  const [createAttachment, setCreateAttachment] = useState(null);

  const API_BASE = process.env.REACT_APP_API_URL;
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  
  const loadTickets = async (page = 1) => {
    try {
      // Determine the correct endpoint based on role
      const roleLC = role?.toLowerCase();
      let endpoint;
      
      if (roleLC === 'superadmin') {
        endpoint = `${API_BASE}/superadmin/support-tickets?page=${page}&limit=${itemsPerPage}${filterStatus ? `&status=${filterStatus}` : ''}${filterOrg ? `&orgId=${filterOrg}` : ''}`;
      } else if (roleLC === 'admin') {
        endpoint = `${API_BASE}/admin/tickets?page=${page}&limit=${itemsPerPage}${filterStatus ? `&status=${filterStatus}` : ''}${filterOrg ? `&orgId=${filterOrg}` : ''}`;
      } else if (roleLC === 'orguser') {
        endpoint = `${API_BASE}/orguser/support-tickets?page=${page}&limit=${itemsPerPage}${filterStatus ? `&status=${filterStatus}` : ''}`;
      } else {
        // Default to admin for backward compatibility
        endpoint = `${API_BASE}/admin/tickets?page=${page}&limit=${itemsPerPage}`;
      }
      
      console.log(`Fetching tickets from: ${endpoint}`);
      const res = await fetch(endpoint, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });
      if (!res.ok) {
        console.error('Failed to load tickets, status:', res.status);
        throw new Error('Failed to load tickets');
      }
      const responseData = await res.json();
      console.log('Tickets response:', responseData);
      
      // Handle different response structures - support both old and new formats
      let list, pagination;
      if (Array.isArray(responseData)) {
        // Old format: direct array (backward compatibility)
        list = responseData;
        setTotalRecords(list.length);
        setTotalPages(Math.ceil(list.length / itemsPerPage));
        setCurrentPage(page);
      } else {
        // New format: { data, pagination }
        list = responseData?.data || [];
        pagination = responseData?.pagination || {};
        setTotalPages(pagination.pages || 1);
        setTotalRecords(pagination.total || list.length);
        setCurrentPage(page);
      }
      
      console.log('Tickets list length:', list.length);
      const mapped = list.map((t) => ({
        id: t.id,
        ticketNumber: t.ticketNumber || t.id?.toString() || 'N/A',
        subject: t.subject || 'No Subject',
        description: t.description || '',
        status: t.status || 'Open',
        createdAt: t.createdAt,
        updatedAt: t.updatedAt,
        attachment: !!t.attachmentPath || !!t.attachment,
        attachmentUrl: t.attachmentUrl || null,
        raisedBy: t.creator?.fullName || t.creator?.email || 'N/A',
        organization: t.organization?.organizationName || 'N/A',
      }));
      console.log('Mapped tickets:', mapped);
      setTickets(mapped);
    } catch (err) {
      console.error('Error loading tickets:', err);
    }
  };

  useEffect(() => {
    loadTickets(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, currentPage, itemsPerPage, filterStatus]);

  useEffect(() => {
    // Client-side filtering for search only (pagination handled by backend now)
    let filtered = tickets;
    if (searchTerm) {
      filtered = filtered.filter(t =>
        (t.ticketNumber || t.id).toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.raisedBy.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredTickets(filtered);
  }, [searchTerm, tickets]);

  const openViewModal = (ticket) => {
    setSelectedTicket(ticket);
    setStatusUpdate(ticket.status);
    setActiveTab('communication');
    setNewMessage('');
    setMessageFile(null);
    setTicketMessages([]);
    setViewModal(true);
    // Load conversation thread
    (async () => {
      try {
        setLoadingMessages(true);
        let endpoint;
        if (isSuperAdmin) endpoint = `${API_BASE}/superadmin/support-tickets/${ticket.id}/messages`;
        else if (isAdmin) endpoint = `${API_BASE}/admin/tickets/${ticket.id}/messages`;
        else endpoint = `${API_BASE}/orguser/support-tickets/${ticket.id}/messages`;
        const res = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          setTicketMessages(data.messages || []);
        }
      } catch (e) {
        console.error('Failed to load messages', e);
      } finally {
        setLoadingMessages(false);
      }
    })();
  };

  const closeModal = () => {
    setViewModal(false);
    setSelectedTicket(null);
    setActiveTab('communication');
    setTicketMessages([]);
    setNewMessage('');
    setMessageFile(null);
  };

  const handleUpdateTicket = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token");
      const endpoint = role === "superadmin"
        ? `${API_BASE}/superadmin/support-tickets/${selectedTicket.id}/status`
        : `${API_BASE}/admin/tickets/${selectedTicket.id}/status`;
      const response = await fetch(endpoint, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: statusUpdate }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to update ticket");
      }

      // Update local state after successful API call
      setTickets(prev =>
        prev.map(t =>
          t.id === selectedTicket.id
            ? { ...t, status: statusUpdate, updatedAt: new Date().toISOString() }
            : t
        )
      );
      
      setSaving(false);
      setSuccessAlert(true);
      setTimeout(() => setSuccessAlert(false), 4000);
      closeModal();
    } catch (error) {
      console.error("Error updating ticket:", error);
      setSaving(false);
      alert("Failed to update ticket. Please try again.");
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !messageFile) return;
    if (!selectedTicket?.id) return;
    if (messageFile) {
      const allowed = ['application/pdf', 'image/png', 'image/jpeg'];
      if (!allowed.includes(messageFile.type)) { alert('Only PDF, PNG, or JPG files allowed'); return; }
      if (messageFile.size > 10 * 1024 * 1024) { alert('File must be ≤ 10MB'); return; }
    }
    try {
      setSendingMessage(true);
      const fd = new FormData();
      if (newMessage.trim()) fd.append('message', newMessage.trim());
      if (messageFile) fd.append('attachment', messageFile);
      let endpoint;
      if (isSuperAdmin) endpoint = `${API_BASE}/superadmin/support-tickets/${selectedTicket.id}/messages`;
      else if (isAdmin) endpoint = `${API_BASE}/admin/tickets/${selectedTicket.id}/messages`;
      else endpoint = `${API_BASE}/orguser/support-tickets/${selectedTicket.id}/messages`;
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const out = await res.json();
      if (!res.ok) throw new Error(out?.message || 'Failed to send message');
      setTicketMessages(prev => [...prev, out.data]);
      setNewMessage('');
      setMessageFile(null);
    } catch (err) {
      alert(err.message || 'Error sending message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    
    // Only orguser can create tickets
    if (role?.toLowerCase() !== 'orguser') {
      alert('Only organization users can create tickets');
      return;
    }

    if (!createFormData.subject?.trim() || !createFormData.description?.trim()) {
      alert('Subject and description are required');
      return;
    }

    if (createAttachment) {
      const allowed = ['application/pdf','image/png','image/jpeg'];
      if (!allowed.includes(createAttachment.type)) {
        alert('Only PDF, PNG, or JPG files allowed');
        return;
      }
      const maxSize = 10 * 1024 * 1024;
      if (createAttachment.size > maxSize) {
        alert('File must be ≤ 10MB');
        return;
      }
    }

    try {
      setSaving(true);
      const fd = new FormData();
      fd.append('subject', createFormData.subject.trim());
      fd.append('description', createFormData.description.trim());
      if (createAttachment) fd.append('attachment', createAttachment);

      const endpoint = `${API_BASE}/orguser/support-tickets`;

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: fd,
      });

      const out = await res.json();
      if (!res.ok) {
        const msg = out?.message || 'Failed to create ticket';
        throw new Error(msg);
      }

      alert('Ticket created successfully');
      setCreateFormData({ subject: '', description: '', organizationId: '' });
      setCreateAttachment(null);
      setShowCreateForm(false);
      setSaving(false);
      setCurrentPage(1); // Reset to first page
      loadTickets(1);
    } catch (err) {
      console.error(err);
      alert(err.message || 'Error creating ticket');
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      Open: { color: "danger", icon: <AiOutlineExclamationCircle /> },
      "In Progress": { color: "warning", icon: <AiOutlineClockCircle /> },
      Resolved: { color: "success", icon: <AiOutlineCheckCircle /> },
      Closed: { color: "secondary", icon: <AiOutlineCheckCircle /> },
    };
    const { color, icon } = map[status] || { color: "info", icon: null };
    return <Badge color={color} className="sa-ticket-status-badge">{icon} {status}</Badge>;
  };

  return (
    <div className="sa-ticket-container">
      {successAlert && (
        <Alert color="success" className="sa-ticket-alert">
          <strong>Ticket {selectedTicket?.id}</strong> updated successfully!
        </Alert>
      )}

      <Row className="mb-4">
        <Col md={role?.toLowerCase() === 'orguser' ? "8" : "12"}>
          <h2 className="sa-ticket-title">
            <AiOutlineSearch className="me-2" /> 
            {role?.toLowerCase() === 'orguser' ? 'My Support Tickets' : 'Support Ticket Management'}
          </h2>
          <p className="text-muted">
            {role?.toLowerCase() === 'orguser' 
              ? 'View and track your support tickets' 
              : 'Manage all support tickets from organizations'}
          </p>
        </Col>
        {role?.toLowerCase() === 'orguser' && (
          <Col md="4" className="text-end">
            <Button color="primary" size="lg" onClick={() => setShowCreateForm(!showCreateForm)}>
              {showCreateForm ? '✕ Cancel' : '➕ Create New Ticket'}
            </Button>
          </Col>
        )}
      </Row>

      {/* Create Ticket Form - Only for OrgUser */}
      {showCreateForm && role?.toLowerCase() === 'orguser' && (
        <Card className="mb-4 border-primary">
          <CardBody>
            <h4 className="mb-4">Create New Support Ticket</h4>
            <form onSubmit={handleCreateTicket}>
              <FormGroup>
                <Label className="fw-bold">Subject *</Label>
                <Input 
                  type="text" 
                  placeholder="Enter ticket subject"
                  value={createFormData.subject} 
                  onChange={(e) => setCreateFormData({...createFormData, subject: e.target.value})} 
                  required 
                />
              </FormGroup>
              <FormGroup>
                <Label className="fw-bold">Description *</Label>
                <Input 
                  type="textarea" 
                  rows="5"
                  placeholder="Describe the issue in detail..."
                  value={createFormData.description} 
                  onChange={(e) => setCreateFormData({...createFormData, description: e.target.value})} 
                  required 
                />
              </FormGroup>
              <FormGroup>
                <Label className="fw-bold">Attachment (optional)</Label>
                <Input 
                  type="file" 
                  accept=".pdf,image/png,image/jpeg" 
                  onChange={(e) => setCreateAttachment(e.target.files?.[0] || null)} 
                />
                <small className="text-muted">Allowed: PDF, PNG, JPG; Max 10MB</small>
              </FormGroup>
              <div className="d-flex gap-2">
                <Button type="submit" color="primary" disabled={saving}>
                  {saving ? 'Creating...' : 'Create Ticket'}
                </Button>
                <Button type="button" color="secondary" outline onClick={() => setShowCreateForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      {/* Stats Cards */}
      <Row className="mb-5 g-4">
        {(() => {
          const today = new Date().toDateString();
          const todayTickets = tickets.filter(t => new Date(t.createdAt).toDateString() === today);
          
          return [
            { label: "Open", count: tickets.filter(t => t.status === "Open").length, color: "danger" },
            { label: "In Progress", count: tickets.filter(t => t.status === "In Progress").length, color: "warning" },
            { label: "Resolved", count: tickets.filter(t => t.status === "Resolved").length, color: "success" },
            { label: "Total Today", count: todayTickets.length, color: "info" },
          ];
        })().map((stat, i) => (
          <Col md="3" key={i}>
            <Card className={`sa-ticket-stat-card bg-gradient-${stat.color} text-white shadow-lg`}>
              <CardBody className="text-center py-4">
                <div className="sa-ticket-stat-icon">
                  {stat.label.includes("Open") && <AiOutlineExclamationCircle size={40} />}
                  {stat.label.includes("Progress") && <AiOutlineClockCircle size={40} />}
                  {stat.label.includes("Resolved") && <AiOutlineCheckCircle size={40} />}
                  {stat.label.includes("Today") && <AiOutlineFilter size={40} />}
                </div>
                <h5 className="mt-3 mb-1">{stat.label}</h5>
                <h2 className="mb-0">{stat.count}</h2>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filters */}
      <Card className="mb-4 sa-ticket-filter-card">
        <CardBody>
          <Row className="g-3 align-items-center">
            <Col md="5">
              <div className="position-relative">
                <AiOutlineSearch className="sa-ticket-search-icon" />
                <Input
                  placeholder="Search by Ticket ID, Subject, User..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="sa-ticket-search-input"
                />
              </div>
            </Col>
            <Col md="3">
              <Input 
                type="select" 
                value={filterStatus} 
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setCurrentPage(1); // Reset to first page on filter change
                }}
              >
                <option value="">All Status</option>
                <option>Open</option>
                <option>In Progress</option>
                <option>Resolved</option>
                <option>Closed</option>
              </Input>
            </Col>
            {role?.toLowerCase() !== 'orguser' && (
              <Col md="3">
                <Input 
                  type="text" 
                  placeholder="Organization ID (optional)"
                  value={filterOrg} 
                  onChange={(e) => {
                    setFilterOrg(e.target.value);
                    setCurrentPage(1); // Reset to first page on filter change
                  }}
                />
              </Col>
            )}
            <Col md={role?.toLowerCase() === 'orguser' ? "4" : "1"}>
              <Button 
                color="primary" 
                className="w-100 sa-ticket-filter-btn"
                onClick={() => loadTickets(1)}
              >
                <AiOutlineFilter /> {role?.toLowerCase() === 'orguser' ? ' Apply Filter' : ''}
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Table */}
      <Card className="sa-ticket-table-card shadow-xl">
        <CardBody className="p-0">
          <div className="table-responsive">
              <table className="table table-hover sa-ticket-table mb-0">
              <thead>
                <tr className="sa-ticket-thead">
                  <th>Ticket ID</th>
                  <th>Subject</th>
                  {role?.toLowerCase() !== 'orguser' && <th>Raised By</th>}
                  {role?.toLowerCase() !== 'orguser' && <th>Organization</th>}
                  <th>Created</th>
                  <th>Status</th>
                  <th>Attachment</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredTickets.map((ticket) => (
                  <tr key={ticket.id} className="sa-ticket-row">
                    <td className="fw-bold text-primary">{ticket.ticketNumber || ticket.id}</td>
                    <td className="sa-ticket-subject">{ticket.subject}</td>
                    {role?.toLowerCase() !== 'orguser' && <td><AiOutlineUser className="me-1" />{ticket.raisedBy}</td>}
                    {role?.toLowerCase() !== 'orguser' && <td>{ticket.organization}</td>}
                    <td>{new Date(ticket.createdAt).toLocaleString()}</td>
                    <td>{getStatusBadge(ticket.status)}</td>
                    <td className="text-center">
                      {ticket.attachment ? (
                        ticket.attachmentUrl ? (
                          <a href={ticket.attachmentUrl} target="_blank" rel="noreferrer">
                            <AiOutlinePaperClip className="text-success" size={22} />
                          </a>
                        ) : (
                          <AiOutlinePaperClip className="text-success" size={22} />
                        )
                      ) : "—"}
                    </td>
                    <td>
                      <Button size="sm" color="link" className="text-primary" onClick={() => openViewModal(ticket)}>
                        <AiOutlineEye size={20} /> View
                      </Button>
                    </td>
                  </tr>
                ))}
                {filteredTickets.length === 0 && (
                  <tr>
                    <td colSpan={role?.toLowerCase() === 'orguser' ? "6" : "8"} className="text-center py-5 text-muted">
                      <div className="d-flex flex-column align-items-center">
                        <AiOutlineSearch size={48} className="mb-3 opacity-50" />
                        <p className="mb-0">No support tickets found</p>
                        <small>Tickets will appear here when organizations submit support requests</small>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {totalRecords > 0 && (
            <div className="d-flex justify-content-between align-items-center px-4 py-3 border-top">
              <div className="text-muted">
                Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalRecords)} to {Math.min(currentPage * itemsPerPage, totalRecords)} of {totalRecords} tickets
              </div>
              <div className="d-flex align-items-center gap-2">
                <div className="d-flex align-items-center gap-2 me-3">
                  <span className="text-muted small">Rows per page:</span>
                  <Input 
                    type="select" 
                    value={itemsPerPage} 
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    style={{ width: '80px', padding: '4px 8px' }}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </Input>
                </div>
                
                <Button 
                  color="light" 
                  size="sm" 
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                >
                  First
                </Button>
                <Button 
                  color="light" 
                  size="sm" 
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => prev - 1)}
                >
                  Previous
                </Button>
                <span className="px-3 text-muted">
                  Page {currentPage} of {totalPages}
                </span>
                <Button 
                  color="light" 
                  size="sm" 
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                >
                  Next
                </Button>
                <Button 
                  color="light" 
                  size="sm" 
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {(isOrgUser || isSuperAdmin || isAdmin) && viewModal && (
        <div className="credit-modal-overlay" onClick={closeModal}>
          <div className="credit-modal-content wide sa-ticket-credit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="credit-modal-header">
              <h2>Ticket {selectedTicket?.ticketNumber || selectedTicket?.id} — {selectedTicket?.subject}</h2>
              <button className="credit-modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="credit-modal-body">

              {/* ── Row 1: Description (left) + Ticket Info (right) ── */}
              <div className="sa-ticket-details-grid" style={{ alignItems: 'start' }}>

                {/* Left column: Description + Update Status (admin only) */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div className="sa-ticket-detail-card">
                    <h4 className="sa-ticket-section-title">Description</h4>
                    <div className="sa-ticket-desc-box">
                      <p className="mb-0">{selectedTicket?.description}</p>
                    </div>
                  </div>
                  {!isOrgUser && (
                    <div className="sa-ticket-detail-card">
                      <h4 className="sa-ticket-section-title">Update Status</h4>
                      <Input
                        type="select"
                        value={statusUpdate}
                        onChange={(e) => setStatusUpdate(e.target.value)}
                        className="sa-ticket-status-select"
                      >
                        <option>Open</option>
                        <option>In Progress</option>
                        <option>Resolved</option>
                        <option>Closed</option>
                      </Input>
                    </div>
                  )}
                </div>

                {/* Right column: Ticket Info */}
                <div className="sa-ticket-detail-card">
                  <h4 className="sa-ticket-section-title">Ticket Info</h4>
                  <div className="sa-ticket-detail-list">
                    <div className="sa-ticket-detail-item">
                      <div className="sa-ticket-detail-label">Ticket ID</div>
                      <div className="sa-ticket-detail-value">{selectedTicket?.ticketNumber || selectedTicket?.id}</div>
                    </div>
                    <div className="sa-ticket-detail-item">
                      <div className="sa-ticket-detail-label">Raised By</div>
                      <div className="sa-ticket-detail-value">{selectedTicket?.raisedBy}</div>
                    </div>
                    <div className="sa-ticket-detail-item">
                      <div className="sa-ticket-detail-label">Organization</div>
                      <div className="sa-ticket-detail-value">{selectedTicket?.organization}</div>
                    </div>
                    <div className="sa-ticket-detail-item">
                      <div className="sa-ticket-detail-label">Status</div>
                      <div className="sa-ticket-detail-value">{getStatusBadge(selectedTicket?.status || 'Open')}</div>
                    </div>
                    <div className="sa-ticket-detail-item">
                      <div className="sa-ticket-detail-label">Created</div>
                      <div className="sa-ticket-detail-value">{selectedTicket?.createdAt && new Date(selectedTicket.createdAt).toLocaleString()}</div>
                    </div>
                    <div className="sa-ticket-detail-item">
                      <div className="sa-ticket-detail-label">Last Updated</div>
                      <div className="sa-ticket-detail-value">{selectedTicket?.updatedAt && new Date(selectedTicket.updatedAt).toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="sa-ticket-divider" />

              {/* ── Row 2: Communication / Attachments tabs ── */}
              <div className="sa-ticket-detail-card" style={{ gridColumn: '1 / -1' }}>

                {/* Tab headers */}
                <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #e9ecef', marginBottom: '16px' }}>
                  {['communication', 'attachments'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '8px 20px',
                        fontWeight: 600,
                        fontSize: '0.88rem',
                        cursor: 'pointer',
                        borderBottom: activeTab === tab ? '2px solid #6f42c1' : '2px solid transparent',
                        marginBottom: '-2px',
                        color: activeTab === tab ? '#6f42c1' : '#666',
                        textTransform: 'capitalize',
                      }}
                    >
                      {tab === 'communication' ? 'Communication' : 'Attachments'}
                    </button>
                  ))}
                </div>

                {/* ── Communication tab ── */}
                {activeTab === 'communication' && (
                  <>
                    {/* Thread */}
                    <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '12px', padding: '4px 2px' }}>
                      {loadingMessages ? (
                        <div className="text-muted text-center py-3" style={{ fontSize: '0.85rem' }}>Loading messages...</div>
                      ) : ticketMessages.filter(m => m.message).length === 0 ? (
                        <div className="text-muted text-center py-3" style={{ fontSize: '0.85rem' }}>No messages yet. Start the conversation below.</div>
                      ) : (
                        ticketMessages.filter(m => m.message).map((msg) => {
                          const isLCS = msg.senderType === 'lcs_team';
                          let senderLabel;
                          if (isLCS) {
                            const rawSender = (msg.senderName || '').trim();
                            const hasRealSender = rawSender && rawSender.toLowerCase() !== 'lcs team';
                            if (isOrgUser) {
                              senderLabel = 'LCS Team';
                            } else {
                              senderLabel = hasRealSender ? `LCS Team — ${rawSender}` : 'LCS Team';
                            }
                          } else {
                            senderLabel = `${msg.senderName} (Org User)`;
                          }
                          return (
                            <div key={msg.id} style={{ display: 'flex', justifyContent: isLCS ? 'flex-end' : 'flex-start', marginBottom: '10px' }}>
                              <div style={{
                                background: isLCS ? '#e8f5e9' : '#f0f4ff',
                                border: `1px solid ${isLCS ? '#c8e6c9' : '#d0d9f0'}`,
                                borderRadius: isLCS ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                                padding: '8px 14px', maxWidth: '75%',
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '4px' }}>
                                  <span style={{ fontWeight: 700, fontSize: '0.78rem', color: isLCS ? '#2e7d32' : '#3949ab' }}>
                                    {senderLabel}
                                  </span>
                                  <span style={{ fontSize: '0.72rem', color: '#999', whiteSpace: 'nowrap' }}>
                                    {new Date(msg.createdAt).toLocaleString()}
                                  </span>
                                </div>
                                <p style={{ margin: 0, fontSize: '0.88rem', whiteSpace: 'pre-wrap' }}>{msg.message}</p>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                    {/* Reply box */}
                    <div style={{ borderTop: '1px solid #eee', paddingTop: '10px' }}>
                      <textarea
                        rows="2"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder={isOrgUser ? 'Write a message to LCS Team...' : 'Write a reply to the customer...'}
                        style={{ width: '100%', resize: 'none', borderRadius: '6px', border: '1px solid #ccc', padding: '8px', fontSize: '0.87rem', marginBottom: '8px' }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={handleSendMessage}
                          disabled={!newMessage.trim() || sendingMessage}
                        >
                          {sendingMessage ? 'Sending...' : 'Send Message'}
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {/* ── Attachments tab ── */}
                {activeTab === 'attachments' && (
                  <>
                    {/* List of all attachments */}
                    <div style={{ marginBottom: '16px' }}>
                      {/* Original ticket attachment */}
                      {selectedTicket?.attachmentUrl && (
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: '10px',
                          padding: '10px 14px', borderRadius: '8px',
                          background: '#f0f4ff', border: '1px solid #d0d9f0', marginBottom: '8px',
                        }}>
                          <AiOutlinePaperClip style={{ color: '#3949ab', flexShrink: 0 }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#3949ab' }}>
                              {selectedTicket.raisedBy} (Org User) — Original Attachment
                            </div>
                            <a href={selectedTicket.attachmentUrl} target="_blank" rel="noreferrer" style={{ fontSize: '0.84rem' }}>
                              View File
                            </a>
                          </div>
                          <span style={{ fontSize: '0.72rem', color: '#999' }}>
                            {selectedTicket?.createdAt && new Date(selectedTicket.createdAt).toLocaleString()}
                          </span>
                        </div>
                      )}

                      {/* Message attachments */}
                      {ticketMessages.filter(m => m.attachmentUrl || m.attachmentPath).map((msg) => {
                        const isLCS = msg.senderType === 'lcs_team';
                        let senderLabel;
                        if (isLCS) {
                          const rawSender = (msg.senderName || '').trim();
                          const hasRealSender = rawSender && rawSender.toLowerCase() !== 'lcs team';
                          if (isOrgUser) {
                            senderLabel = 'LCS Team';
                          } else {
                            senderLabel = hasRealSender ? `LCS Team — ${rawSender}` : 'LCS Team';
                          }
                        } else {
                          senderLabel = `${msg.senderName} (Org User)`;
                        }
                        return (
                          <div key={msg.id} style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            padding: '10px 14px', borderRadius: '8px',
                            background: isLCS ? '#e8f5e9' : '#f0f4ff',
                            border: `1px solid ${isLCS ? '#c8e6c9' : '#d0d9f0'}`,
                            marginBottom: '8px',
                          }}>
                            <AiOutlinePaperClip style={{ color: isLCS ? '#2e7d32' : '#3949ab', flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: isLCS ? '#2e7d32' : '#3949ab' }}>
                                {senderLabel}
                              </div>
                              {msg.attachmentUrl ? (
                                <a href={msg.attachmentUrl} target="_blank" rel="noreferrer" style={{ fontSize: '0.84rem' }}>View File</a>
                              ) : (
                                <span style={{ fontSize: '0.84rem', color: '#999' }}>File unavailable</span>
                              )}
                              {msg.message && <div style={{ fontSize: '0.78rem', color: '#666', marginTop: '2px' }}>{msg.message}</div>}
                            </div>
                            <span style={{ fontSize: '0.72rem', color: '#999', whiteSpace: 'nowrap' }}>
                              {new Date(msg.createdAt).toLocaleString()}
                            </span>
                          </div>
                        );
                      })}

                      {!selectedTicket?.attachmentUrl && ticketMessages.filter(m => m.attachmentUrl || m.attachmentPath).length === 0 && (
                        <div className="text-muted text-center py-3" style={{ fontSize: '0.85rem' }}>No attachments yet.</div>
                      )}
                    </div>

                    {/* Upload a new attachment */}
                    <div style={{ borderTop: '1px solid #eee', paddingTop: '10px' }}>
                      <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: '8px', color: '#444' }}>Send an attachment</div>
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Optional message with the file..."
                        style={{ width: '100%', borderRadius: '6px', border: '1px solid #ccc', padding: '6px 10px', fontSize: '0.85rem', marginBottom: '8px' }}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <input
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg"
                          onChange={(e) => setMessageFile(e.target.files[0] || null)}
                          style={{ fontSize: '0.82rem', flex: 1, minWidth: 0 }}
                        />
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={handleSendMessage}
                          disabled={!messageFile || sendingMessage}
                        >
                          {sendingMessage ? 'Sending...' : 'Send'}
                        </button>
                      </div>
                      <small className="text-muted" style={{ fontSize: '0.75rem' }}>PDF, PNG or JPG — max 10 MB</small>
                    </div>
                  </>
                )}

              </div>
            </div>
            <div className="credit-modal-footer">
              {isOrgUser ? (
                <Button color="secondary" onClick={closeModal}>Close</Button>
              ) : (
                <>
                  <Button color="secondary" outline onClick={closeModal}>Cancel</Button>
                  <Button color="primary" onClick={handleUpdateTicket} disabled={saving}>
                    {saving ? 'Saving...' : 'Update Ticket'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}


    </div>
  );
}